import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../hooks/useAuth';
import { Modal, ModalFooter } from '../ui/modal';
import { FormGroup, FormLabel, FormInput, FormTextarea, FormSelect } from '../ui/form';

function AddProjectModal({ show, handleClose, onProjectAdded }) {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [projectGroups, setProjectGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const addToast = useToast();

  useEffect(() => {
    const fetchProjectGroups = async () => {
      const { data, error } = await supabase
        .from('project_groups')
        .select('id, name');

      if (error) {
        console.error('Error fetching project groups:', error);
        addToast('Error fetching project groups: ' + error.message, 'danger');
      } else {
        setProjectGroups(data);
      }
    };

    fetchProjectGroups();
  }, [addToast]);

  const handleSave = async () => {
    if (!name.trim()) {
      addToast('Project name is required', 'danger');
      return;
    }

    if (!selectedGroupId) {
      addToast('Please select a project group', 'danger');
      return;
    }

    const { data, error } = await supabase
      .from('projects')
      .insert([{ name, description, project_group_id: selectedGroupId, user_id: user.id }])
      .select('id, name, description, project_groups!inner (id, name), tasks (id)');

    if (error) {
      console.error('Error adding project:', error);
      addToast('Error adding project: ' + error.message, 'danger');
    } else if (data && data.length > 0) {
      addToast('Project added successfully', 'success');
      onProjectAdded(data[0]);
      handleClose();
    } else {
      console.error('Unexpected response format:', data);
      addToast('Unexpected response format when adding project', 'danger');
    }
  };

  return (
    <Modal isOpen={show} onClose={handleClose} title="Add Project">
      <div className="space-y-4">
        <FormGroup>
          <FormLabel htmlFor="name">Name *</FormLabel>
          <FormInput
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter project name"
          />
        </FormGroup>
        
        <FormGroup>
          <FormLabel htmlFor="description">Description</FormLabel>
          <FormTextarea
            id="description"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter project description"
          />
        </FormGroup>
        
        <FormGroup>
          <FormLabel htmlFor="projectGroup">Project Group *</FormLabel>
          <FormSelect
            id="projectGroup"
            value={selectedGroupId}
            onChange={(e) => setSelectedGroupId(e.target.value)}
          >
            <option value="">Select a project group</option>
            {projectGroups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </FormSelect>
        </FormGroup>
      </div>
      
      <ModalFooter>
        <button
          type="button"
          onClick={handleClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Save Project
        </button>
      </ModalFooter>
    </Modal>
  );
}

export default AddProjectModal;